import React, { useEffect, useState, useContext } from 'react';
import { Button, Box, Modal, Typography } from '@mui/material';
import { AppContext } from "/@/app/AppContext.js";

export const InstallComponent = ({ showDebug = true }) => {
  const { userData } = useContext(AppContext);
  const showDebugData = (userData.is_staff || userData.is_superuser) && showDebug;

  /** ----------------- ENV DETECTION ----------------- */
  const userAgent = navigator.userAgent;

  const detectBrowser = () => {
    if (userAgent.includes('Firefox')) return "firefox";
    if (userAgent.includes('Edg')) return "edge";
    if (userAgent.includes('Chrome')) return "chrome";
    if (userAgent.includes('Safari')) return "safari";
    return "other";
  };

  const browser = detectBrowser();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const resizeHandler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  /** ----------------- PWA DETECTION ----------------- */
  const getIsPWA = () =>
    window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

  const [isPWA, setIsPWA] = useState(getIsPWA());
  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const handler = (e) => setIsPWA(e.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  /** ----------------- INSTALL STATE TRACKING ----------------- */
  const [wasInstalled, setWasInstalled] = useState(localStorage.getItem('pwa-installed') === 'true');
  useEffect(() => {
    if (isPWA) {
      localStorage.setItem('pwa-installed', 'true');
      setWasInstalled(true);
    }
  }, [isPWA]);
  const wasRemoved = wasInstalled && !isPWA;

  /** ----------------- CHROME INSTALL PROMPT ----------------- */
  const [installEvent, setInstallEvent] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault(); // prevent auto-prompt
      setInstallEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  /** ----------------- MODAL STATE ----------------- */
  const [modalOpen, setModalOpen] = useState(false);

  /** ----------------- INSTALL ACTION ----------------- */
  const handleInstall = async () => {
    if (browser === "chrome" && installEvent) {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      console.log('Install result:', choice.outcome);
      setInstallEvent(null);
    } else {
      setModalOpen(true); // Safari/Firefox instructions
    }
  };

  /** ----------------- DECIDE IF BUTTON SHOULD SHOW ----------------- */
  const canShowInstall =
    !isPWA &&
    ((browser === "chrome" && !!installEvent) || (browser !== "chrome" && isMobile));

  if (!canShowInstall) {
    return showDebugData ? <Debug browser={browser} isMobile={isMobile} isPWA={isPWA} wasInstalled={wasInstalled} /> : null;
  }

  /** ----------------- RENDER ----------------- */
  return (
    <>
      <Box sx={{ textAlign: "center", p: 1 }}>
        <Button variant="contained" onClick={handleInstall}>
          {wasRemoved ? "Установить снова" : "Установить Nestarenie.Expert"}
        </Button>
        {showDebugData && <Debug browser={browser} isMobile={isMobile} isPWA={isPWA} wasInstalled={wasInstalled} />}
      </Box>

      <InstallInstructionsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        browser={browser}
      />
    </>
  );
};

/** ----------------- DEBUG ----------------- */
const Debug = ({ browser, isMobile, isPWA, wasInstalled }) => (
  <Box sx={{ fontSize: "10px", mt: 1 }}>
    browser: {browser}<br />
    mobile: {String(isMobile)}<br />
    isPWA: {String(isPWA)}<br />
    wasInstalled: {String(wasInstalled)}
  </Box>
);

/** ----------------- MODAL ----------------- */
const InstallInstructionsModal = ({ open, onClose, browser }) => (
  <Modal open={open} onClose={onClose}>
    <Box sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '90vw',
      maxWidth: 500,
      bgcolor: 'white',
      p: 3,
      borderRadius: 2
    }}>
      <Typography variant="h6" gutterBottom>Установка приложения</Typography>

      {browser === "safari" && <Typography>Нажмите "Поделиться" → "На экран «Домой»"</Typography>}
      {browser === "firefox" && <Typography>Меню → "Установить"</Typography>}

      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Button onClick={onClose}>Закрыть</Button>
      </Box>
    </Box>
  </Modal>
);
